import { useEffect, useState } from "react";
import { api, fmtDistance, fmtOsaEnd } from "../api";
import {
  geoMessage, hasVisitFilters,
  type Coords, type Cuisine, type District, type GeoState, type Tag, type VenueListItem, type VisitFilters,
} from "../types";
import Illustration from "../components/Illustration";

/** 三大區域，順序同下拉顯示一致。實際歸類由後端負責（backend/hk_region.py）。 */
const REGIONS = ["香港島", "九龍", "新界"];

const AREAS = [
  { k: "open_air", label: "無蓋露天" },
  { k: "covered_terrace", label: "有蓋" },
  { k: "rooftop", label: "天臺" },
  { k: "indoor", label: "室內" },
];

const VERDICTS = [
  { k: "yes", label: "食得" },
  { k: "no", label: "唔得" },
  { k: "unsure", label: "唔確定" },
];

/**
 * 首頁 = 三個入口，跟已確認嘅設計：
 *   ① 附近餐廳（主）—— 行先，直接睇最近三間
 *   ② 今晚想食乜（次）—— 菜式用下拉選單，唔可以 list 晒出嚟
 *   ③ 我到訪記錄 —— 用自己落過嘅備註類別／結論去搵返餐廳
 * 「可食雪茄」係前提，所以剩返嘅篩選軸只有菜式、距離，同我自己嘅記錄。
 *
 * 冇定位嗰陣，後端係按「露天位最遲收」排，唔係按距離 —— 所以呢度唔可以
 * 再寫「用距離排序」。標籤要講返實際發生緊乜，唔係就會變成一個講大話嘅
 * 「附近餐廳」。
 */
export default function Home({
  coords,
  geoState,
  geoDetail,
  onAskGeo,
  onPickDistrict,
  onNearby,
  onCuisine,
  onVisitSearch,
  onOpen,
}: {
  coords: Coords;
  geoState: GeoState;
  geoDetail?: string;
  onAskGeo: () => void;
  onPickDistrict: (c: { lat: number; lng: number }) => void;
  onNearby: () => void;
  onCuisine: (key: string, label: string, district?: string, filters?: VisitFilters,
              filterLabel?: string) => void;
  onVisitSearch: (filters: VisitFilters, filterLabel?: string) => void;
  onOpen: (id: number) => void;
}) {
  const [near, setNear] = useState<VenueListItem[] | null>(null);
  const [showDistricts, setShowDistricts] = useState(false);
  const [districts, setDistricts] = useState<District[] | null>(null);
  const [dq, setDq] = useState("");
  const [pickDistrict, setPickDistrict] = useState("");
  const [pickRegion, setPickRegion] = useState("");

  // 區份按大區域分組。對唔到區域嘅（region === null）一律放入「其他」，
  // 唔會靜靜歸入某一區。
  const districtsForRegion = (districts ?? []).filter(
    (d) => !pickRegion || d.region === pickRegion,
  );

  // 桌面電腦冇 GPS，Chrome 又只可以靠 WiFi 定位，用網線嘅機根本攞唔到 fix。
  // 呢種情況用戶點改設定都冇用，所以要有一條手動路徑。
  async function loadDistricts() {
    if (districts) return districts;
    try {
      const d = await api.districts();
      setDistricts(d);
      return d;
    } catch {
      setDistricts([]);
      return [];
    }
  }
  async function openDistricts() {
    setShowDistricts(true);
    await loadDistricts();
  }
  const [cuisines, setCuisines] = useState<Cuisine[] | null>(null);
  const [pick, setPick] = useState("");
  const [err, setErr] = useState("");

  // 我到訪記錄嘅篩選。同菜式可以一齊用：揀咗菜式就係「呢類菜 + 我嘅記錄」，
  // 唔揀菜式就係「全部餐廳 + 我嘅記錄」。
  const [vf, setVf] = useState<VisitFilters>({});
  const [tags, setTags] = useState<Tag[] | null>(null);

  useEffect(() => {
    let live = true;
    Promise.all([
      api.venues({ coords, limit: 3 }),
      api.districts().catch(() => [] as District[]),
      api.tags().catch(() => [] as Tag[]),
    ])
      .then(([v, d, t]) => {
        if (!live) return;
        setNear(v);
        setDistricts(d);
        setTags(t);
      })
      .catch((e) => live && setErr(e instanceof Error ? e.message : "載入失敗"));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coords?.lat, coords?.lng]);

  // 菜式清單要跟住地區重新載入 —— 個數一定要反映「呢一區有幾多間」。
  // 冇呢個嘅話用戶揀「中環」之後，下拉仍然寫「港式（67 間）」但實際 0 間。
  useEffect(() => {
    let live = true;
    api
      .cuisines(pickDistrict || undefined)
      .then((c) => {
        if (!live) return;
        setCuisines(c);
        // 原本揀緊嘅菜式喺呢一區可能冇 —— 清空，唔可以留住一個搵唔到嘢嘅選擇
        setPick((p) => (p && c.some((x) => x.key === p) ? p : ""));
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [pickDistrict]);

  const openNow = near?.filter((v) => v.open_now).length ?? 0;
  const set = (patch: Partial<VisitFilters>) => setVf((f) => ({ ...f, ...patch }));
  // 未用過嘅標籤唔會出現喺篩選 —— 同菜式一樣嘅規矩：揀咗一定搵唔到嘢嘅
  // 選項唔應該出現喺清單。但記錄到訪嗰陣仍然全部揀得到（LogSheet）。
  const usableTags = (tags ?? []).filter((t) => t.venue_count > 0);
  const vfOn = hasVisitFilters(vf);

  // 人話描述，等列表畫面唔使再攞一次標籤清單就講得出而家篩緊乜。
  const vfLabel = (() => {
    const parts: string[] = [];
    const t = (tags ?? []).find((x) => x.key === vf.tag);
    if (t) parts.push(t.label_zh);
    const v = VERDICTS.find((x) => x.k === vf.verdict);
    if (v) parts.push(v.label);
    const a = AREAS.find((x) => x.k === vf.area);
    if (a) parts.push(a.label);
    if (vf.who) parts.push(vf.who === "me" ? "你去" : "朋友去");
    if (vf.visited === "yes") parts.push("去過");
    if (vf.visited === "no") parts.push("未去過");
    return parts.join("　·　");
  })();

  return (
    <>
      <div className="sign">
        <div className="sign__where">
          {coords ? "以你而家位置為中心" : "未定位"}
          {near && `　·　${coords ? "最近" : "露天位最遲收"} ${near.length} 間`}
        </div>
        <div className="sign__title">今晚去邊食</div>
      </div>

      <div className="app__body">
        {err && <div className="err">{err}</div>}
        <Illustration />

        {/* ① 附近餐廳 —— 主選項 */}
        <div className="opt opt--lead">
          <div className="opt__head">
            <span className="opt__label">附近餐廳</span>
            <span className="opt__count">
              {openNow > 0 ? `${openNow} 間開緊` : "全部有露天位"}
            </span>
          </div>
          <div className="opt__body">
            {/* 位置控制 —— 一定要「永遠見到」。
                之前成個定位區塊只喺 !coords 時 render，所以定位一成功，
                用戶就完全冇位置可以睇、可以改，感覺好似「定位功能唔見咗」。 */}
            <div className="locbar">
              <button className="locbar__main" onClick={openDistricts}>
                <span className="locbar__pin">◎</span>
                <span className="locbar__txt">
                  {coords ? "以你而家位置為中心" : "未定位"}
                </span>
                <span className="locbar__act">改</span>
              </button>
            </div>

            {showDistricts && (
              <div className="districtpick">
                <button className="btn" style={{ marginBottom: 8 }}
                        onClick={() => { setShowDistricts(false); onAskGeo(); }}>
                  用我而家嘅位置
                </button>
                <div className="districtpick__hint">
                  或者揀一個區，當作你而家喺嗰度。桌面電腦冇 GPS 嘅時候用呢個。
                </div>
                <input className="field" placeholder="搵區名…" value={dq}
                       onChange={(e) => setDq(e.target.value)} />
                <div className="districtpick__list">
                  {(districts ?? [])
                    .filter((d) => !dq || d.district.includes(dq))
                    .map((d) => (
                      <button key={d.district} className="districtopp"
                              onClick={() => { setShowDistricts(false); onPickDistrict({ lat: d.lat, lng: d.lng }); }}>
                        <span>{d.district}</span>
                        <span className="districtopp__n">{d.venue_count} 間</span>
                      </button>
                    ))}
                  {districts && districts.length === 0 && (
                    <div className="districtpick__hint">攞唔到區份清單。</div>
                  )}
                  {!districts && <div className="districtpick__hint">載入緊…</div>}
                </div>
              </div>
            )}

            {!coords && (
              <div className="notice notice--geo">
                <div>{geoMessage(geoState) ?? "未攞到位置"}</div>
                {geoState !== "needGesture" && (
                  <div style={{ marginTop: 5, fontSize: 11 }}>
                    所以以下三間係「露天位最遲收」，唔係你附近嘅三間。
                  </div>
                )}
                {geoDetail && <div className="notice__diag">{geoDetail}</div>}
                <button className={`btn ${geoState === "needGesture" ? "" : "btn--plain"}`}
                        style={{ marginTop: 8 }}
                        onClick={onAskGeo}>
                  {geoState === "needGesture" ? "用我而家嘅位置" : "再試定位"}
                </button>
              </div>
            )}
            {near === null ? (
              <div className="loading">載入緊…</div>
            ) : near.length === 0 ? (
              <div className="notice">附近搵唔到，轉用菜式搵</div>
            ) : (
              near.map((v) => (
                <button className="row" key={v.id} onClick={() => onOpen(v.id)}>
                  <span className="row__name">{v.name}</span>
                  <span className="row__dist">{fmtDistance(v.distance_m)}</span>
                  <span className="row__time">
                    <span className="row__timelabel">露天位</span>
                    {fmtOsaEnd(v)}
                    {v.osa_multi && "*"}
                  </span>
                </button>
              ))
            )}
            {near?.some((v) => v.osa_multi) && (
              <div style={{ padding: "6px 12px 0", fontSize: 9.5, color: "var(--ink2)" }}>
                * 牌照時段有幾種（例如午市＋晚市），以上係最遲一個
              </div>
            )}
          </div>
          <div style={{ padding: "0 12px 12px" }}>
            <button className="btn" onClick={onNearby}>
              睇全部附近餐廳
            </button>
          </div>
        </div>

        {/* ② 今晚想食乜 —— 菜式用下拉選單 */}
        <div className="opt">
          <div className="opt__head">
            <span className="opt__label">今晚想食乜</span>
            <span className="opt__count">用菜式搵</span>
          </div>
          <div className="opt__body opt__body--pad">
            {/* 先揀大區域，再揀區 —— 45 個區平鋪太長。 */}
            <div className="field">
              <label className="field__label" htmlFor="region">區域</label>
              <div className="selectwrap">
                <select
                  id="region"
                  className="select"
                  value={pickRegion}
                  onChange={(e) => {
                    setPickRegion(e.target.value);
                    setPickDistrict("");   // 轉區域就要重揀區，唔可以留住上一個區域嘅區
                  }}
                >
                  <option value="">全部區域</option>
                  {REGIONS.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="district">地區</label>
              <div className="selectwrap">
                <select
                  id="district"
                  className="select"
                  value={pickDistrict}
                  onChange={(e) => setPickDistrict(e.target.value)}
                >
                  <option value="">全部地區</option>
                  {districtsForRegion.map((d) => (
                    <option key={d.district} value={d.district}>
                      {d.district}（{d.venue_count} 間）
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="cuisine">菜式種類</label>
              <div className="selectwrap">
                <select
                  id="cuisine"
                  className="select"
                  value={pick}
                  onChange={(e) => setPick(e.target.value)}
                >
                  <option value="">請揀菜式…</option>
                  {cuisines?.map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.label_zh}（{c.venue_count} 間）
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button
              className="btn btn--ghost"
              disabled={!pick}
              onClick={() => {
                if (!pick) return;
                const label = cuisines?.find((c) => c.key === pick)?.label_zh ?? pick;
                // 我到訪記錄嘅篩選會一齊帶過去 —— 呢兩樣係夾得埋嘅。
                onCuisine(pick, label, pickDistrict || undefined,
                          hasVisitFilters(vf) ? vf : undefined,
                          hasVisitFilters(vf) ? vfLabel : undefined);
              }}
            >
              搵呢一類
            </button>
          </div>
        </div>

        {/* ③ 我到訪記錄 —— 用自己落過嘅備註類別／結論搵返餐廳 */}
        <div className="opt">
          <div className="opt__head">
            <span className="opt__label">我到訪記錄</span>
            <span className="opt__count">
              {vfOn ? "條件已設" : "用我落過嘅嘢搵"}
            </span>
          </div>
          <div className="opt__body opt__body--pad">
            <div className="field">
              <label className="field__label">去過未去過</label>
              <div className="opts">
                {([["", "全部"], ["yes", "去過"], ["no", "未去過"]] as const).map(([k, label]) => (
                  <button
                    key={k || "all"}
                    className={`opts__item ${(vf.visited ?? "") === k ? "opts__item--on" : ""}`}
                    onClick={() => set({ visited: k })}
                    aria-pressed={(vf.visited ?? "") === k}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="vf-tag">備註類別</label>
              <div className="selectwrap">
                <select
                  id="vf-tag"
                  className="select"
                  value={vf.tag ?? ""}
                  onChange={(e) => set({ tag: e.target.value || undefined })}
                >
                  <option value="">全部備註類別</option>
                  {/* 全部列出嚟，數目照寫（0 間就 0 間）—— 之前跟菜式嗰條「0 間
                      收埋」規矩，結果未記過到訪嘅人見到一個完全空白嘅下拉，
                      根本唔知有呢個功能。0 間嘅照列但停用：揀咗一定 0 結果。 */}
                  {(tags ?? []).map((t) => (
                    <option key={t.key} value={t.key} disabled={t.venue_count === 0}>
                      {t.label_zh}（{t.venue_count} 間）
                    </option>
                  ))}
                </select>
              </div>
              {tags && usableTags.length === 0 && (
                <div className="field__hint">
                  你仲未記錄過任何到訪，所以 15 個類別暫時都用唔到。
                  入一間餐廳 → 撳「記錄今日到訪」→ 揀返類別，佢就會喺度著返。
                </div>
              )}
            </div>

            <div className="grid2">
              <div className="field">
                <label className="field__label" htmlFor="vf-verdict">食唔食得</label>
                <div className="selectwrap">
                  <select
                    id="vf-verdict"
                    className="select"
                    value={vf.verdict ?? ""}
                    onChange={(e) => set({ verdict: e.target.value || undefined })}
                  >
                    <option value="">全部</option>
                    {VERDICTS.map((v) => (
                      <option key={v.k} value={v.k}>{v.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="field">
                <label className="field__label" htmlFor="vf-area">坐喺邊</label>
                <div className="selectwrap">
                  <select
                    id="vf-area"
                    className="select"
                    value={vf.area ?? ""}
                    onChange={(e) => set({ area: e.target.value || undefined })}
                  >
                    <option value="">全部</option>
                    {AREAS.map((a) => (
                      <option key={a.k} value={a.k}>{a.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="vf-who">邊個去</label>
              <div className="selectwrap">
                <select
                  id="vf-who"
                  className="select"
                  value={vf.who ?? ""}
                  onChange={(e) => set({ who: e.target.value || undefined })}
                >
                  <option value="">全部</option>
                  <option value="me">你</option>
                  <option value="friend">朋友</option>
                </select>
              </div>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="btn btn--plain"
                style={{ flex: 1 }}
                disabled={!vfOn}
                onClick={() => setVf({})}
              >
                清除
              </button>
              <button
                className="btn btn--ghost"
                style={{ flex: 2 }}
                disabled={!vfOn}
                onClick={() => onVisitSearch(vf, vfLabel)}
              >
                搵我記錄過嘅餐廳
              </button>
            </div>
            <div className="field__hint" style={{ marginTop: 8 }}>
              所有條件要落喺<b>同一次</b>到訪 —— 例如「食得 + 蚊多」唔會中一間
              「一次食得、另一次蚊多」嘅餐廳。
            </div>
          </div>
        </div>

      </div>
    </>
  );
}
